import type { SttRealtimeSession } from '@hub/contracts'

/**
 * One dictation session's provider adapter (board card loap). The hook
 * (`web/lib/use-dictation.ts`) owns the microphone permission, the timers,
 * the limits and the auto-stop; a transport owns one socket and the
 * provider's message shape. Nothing outside `web/lib/dictation/*` may branch
 * on the provider.
 */

export class DictationError extends Error {
  constructor(
    message: string,
    public readonly reason: 'unavailable' | 'mic_denied' | 'failed' | 'credits_exhausted'
  ) {
    super(message)
    this.name = 'DictationError'
  }
}

export interface DictationHandlers {
  /** Live, still-changing tail — replaces the previous partial. */
  onPartial(text: string): void
  /**
   * Everything the provider has finalized in this session so far — a
   * snapshot, not a delta. The transport does the joining because only it
   * knows whether its pieces are utterances (ElevenLabs: join with a space)
   * or tokens carrying their own leading whitespace (Soniox: join with '').
   */
  onCommitted(text: string): void
  onError(err: DictationError): void
  /** The socket is gone — closed by us, the provider or the network. */
  onClose(): void
}

export interface DictationTransport {
  /**
   * Opens the socket on an already-granted microphone stream (the hook
   * requests it first so the short-lived session credential is minted after
   * the permission prompt, not before). Resolves once the provider accepted
   * the session.
   */
  start(session: SttRealtimeSession, mic: MediaStream, handlers: DictationHandlers): Promise<void>
  /**
   * Flush what is buffered, resolve when the provider has committed it (or
   * after `timeoutMs`); then close.
   */
  flush(timeoutMs: number): Promise<void>
  close(): void
}
